import { FC, useEffect, useMemo, useState } from 'react';

import { DownloadIcon } from '@heroicons/react/outline';
import { GiftDeliveryForRebatePending } from '@shared/api/giftDelivery/giftDelivery.interface';
import { GiftDeliveryForAdmin } from '@shared/api/giftDelivery/giftDelivery.interface';
import { Loading } from '@shared/components/Loading';

import { getKstDateParts, toYmd } from '../../utils/rebate-settlement.utils';
import {
  computeRebateRow,
  computeRebateTotals,
  RebateTableEntry,
} from '../../utils/rebate-table.utils';
import { exportRebateRowsExcel } from '../../utils/rebateSettlementExcel';
import { getSettlementBrandId } from '../../utils/settlement.utils';
import { RebateSettlementTable } from './RebateSettlementTable';

// ─────────────────────────────────────────────
// RebateDeliveredSection
//
// 아직 정산되지 않은(rebateSettlement=null) 배송완료 건을 정산 예정과 동일한
// 테이블로 보여준다. 업체(giftItemBrand)·배송완료일 기간으로 필터링할 수 있다.
// ─────────────────────────────────────────────

/** 업체(giftItemBrand)가 비어있는 건을 묶는 가상 필터 키 */
const NO_BRAND = '__none__';

/** `RebateDeliveredSection` 컴포넌트의 props */
export interface RebateDeliveredSectionProps {
  /** 배송완료 내역 목록 (undefined이면 로딩 전 상태) */
  deliveredEntries: GiftDeliveryForAdmin[] | undefined;
  /** 데이터 로딩 중 여부 */
  loading: boolean;
}

export const RebateDeliveredSection: FC<RebateDeliveredSectionProps> = ({
  deliveredEntries,
  loading,
}) => {
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  /** 배송완료일 기간 빠른 선택 (이번달/지난달) */
  const setMonthRange = (offset: number) => {
    const { year, month } = getKstDateParts();
    const base = new Date(year, month - 1 + offset, 1);
    const first = new Date(base.getFullYear(), base.getMonth(), 1);
    const last = new Date(base.getFullYear(), base.getMonth() + 1, 0);
    setDateFrom(toYmd(first));
    setDateTo(toYmd(last));
  };

  const clearDateRange = () => {
    setDateFrom('');
    setDateTo('');
  };

  // 업체(giftItemBrand) 필터 칩 목록 — 전체 배송완료 건에서 추출한다(기간 필터 무관).
  const brandOptions = useMemo(() => {
    const map = new Map<string, string>();
    let hasNoBrand = false;
    (deliveredEntries ?? []).forEach((d) => {
      const id = getSettlementBrandId(d);
      if (id) {
        const name =
          typeof d.giftItemBrand === 'object' && d.giftItemBrand
            ? d.giftItemBrand.name
            : '';
        map.set(id, name || id);
      } else {
        hasNoBrand = true;
      }
    });
    const options = Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    if (hasNoBrand) options.push({ id: NO_BRAND, name: '빈업체' });
    return options;
  }, [deliveredEntries]);

  // 데이터가 갱신돼 선택했던 업체가 사라지면 '전체'로 되돌린다.
  useEffect(() => {
    if (selectedBrand !== 'all' && !brandOptions.some((b) => b.id === selectedBrand)) {
      setSelectedBrand('all');
    }
  }, [brandOptions, selectedBrand]);

  // 배송완료: 수량은 delivery.quantity 기준 + 업체·배송완료일 기간 필터
  const tableEntries = useMemo<RebateTableEntry[]>(() => {
    const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const to = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;

    return (deliveredEntries ?? [])
      .filter((d) => {
        // 업체 필터
        if (selectedBrand !== 'all') {
          const brandId = getSettlementBrandId(d) || NO_BRAND;
          if (brandId !== selectedBrand) return false;
        }
        // 배송완료일 기간 필터
        if (!from && !to) return true;
        const deliveredAt = d.shipping?.deliveredAt;
        if (!deliveredAt) return false;
        const date = new Date(deliveredAt);
        if (from && date < from) return false;
        if (to && date > to) return false;
        return true;
      })
      .map((d) => ({
        delivery: d as unknown as GiftDeliveryForRebatePending,
        quantity: d.quantity,
        hasSettlementInfo: true,
      }));
  }, [deliveredEntries, selectedBrand, dateFrom, dateTo]);

  const rows = useMemo(() => tableEntries.map(computeRebateRow), [tableEntries]);
  const totals = useMemo(() => computeRebateTotals(rows), [rows]);

  return (
    <div>
      <div className="mb-4 flex items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold text-gray-800">
            미정산 배송 내역({totals.totalCount}건)
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            아직 정산되지 않은 배송완료 내역입니다.
          </p>
        </div>
        <button
          onClick={() =>
            exportRebateRowsExcel(rows, totals, '미정산배송내역.xlsx', '미정산배송내역')
          }
          disabled={rows.length === 0}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <DownloadIcon className="h-4 w-4" />
          엑셀 다운로드
        </button>
      </div>

      {/* 업체(공급사) 필터 */}
      {brandOptions.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedBrand('all')}
            className={`rounded-full border px-3 py-1 text-sm ${
              selectedBrand === 'all'
                ? 'border-indigo-600 bg-indigo-600 text-white'
                : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            전체
          </button>
          {brandOptions.map((b) => (
            <button
              key={b.id}
              onClick={() => setSelectedBrand(b.id)}
              className={`rounded-full border px-3 py-1 text-sm ${
                selectedBrand === b.id
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {b.name}
            </button>
          ))}
        </div>
      )}

      {/* 배송완료일 기간 필터 */}
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-gray-500">배송완료일</span>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="rounded border border-gray-300 px-2 py-1"
        />
        <span className="text-gray-400">~</span>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="rounded border border-gray-300 px-2 py-1"
        />
        <button
          onClick={() => setMonthRange(0)}
          className="rounded-full border border-gray-300 bg-white px-3 py-1 text-gray-600 hover:bg-gray-50"
        >
          이번달
        </button>
        <button
          onClick={() => setMonthRange(-1)}
          className="rounded-full border border-gray-300 bg-white px-3 py-1 text-gray-600 hover:bg-gray-50"
        >
          지난달
        </button>
        <button
          onClick={clearDateRange}
          className="rounded-full border border-gray-300 bg-white px-3 py-1 text-gray-600 hover:bg-gray-50"
        >
          전체기간
        </button>
      </div>

      {/* 요약 카드 */}
      <div className="mb-4 grid grid-cols-4 gap-3">
        <div className="flex flex-col justify-center rounded-lg bg-gray-50 p-4 text-center">
          <p className="text-2xl font-bold text-gray-700">
            {totals.totalPriceRetail.toLocaleString()}
          </p>
          <p className="text-sm text-gray-500">총 판매가</p>
        </div>
        <div className="flex flex-col justify-center rounded-lg bg-gray-50 p-4 text-center">
          <p className="text-2xl font-bold text-gray-700">
            {totals.totalPriceWhole.toLocaleString()}
          </p>
          <p className="text-sm text-gray-500">총 상품원가</p>
        </div>
        <div className="flex flex-col justify-center rounded-lg bg-gray-50 p-4 text-center">
          <p className="text-2xl font-bold text-gray-700">
            {totals.totalFee.toLocaleString()}
          </p>
          <p className="text-sm text-gray-500">총 수수료</p>
        </div>
        <div className="flex flex-col justify-center rounded-lg bg-gray-50 p-4 text-center">
          <p className="text-2xl font-bold text-rose-700">
            {totals.totalAmount.toLocaleString()}
          </p>
          <p className="text-sm text-gray-500">총 수익금 (VAT 포함)</p>
        </div>
      </div>

      {loading ? (
        <div className="center-box py-16">
          <Loading />
        </div>
      ) : (
        <RebateSettlementTable
          rows={rows}
          totals={totals}
          emptyMessage="배송완료된 건이 없습니다."
        />
      )}
    </div>
  );
};
